import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import styled from 'styled-components';
import { Button, DateTime, Icon, Tooltip } from '~/components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import bambooIcon from '~/assets/public/bamboohr-icon.svg';
import expensifyIcon from '~/assets/public/expensify-icon.svg';
import hubspotIcon from '~/assets/public/hubspot-icon.svg';
import qboIcon from '~/assets/public/qbo-icon.png';
import ripplingIcon from '~/assets/public/rippling-icon.svg';
import salesforceIcon from '~/assets/public/salesforce-logo.png';
import slackIcon from '~/assets/public/slack-icon.svg';
import xeroIcon from '~/assets/public/xero-icon.png';

const Details = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 1.25rem;
  padding-top: 0;
  border-top: solid 1px ${colors.grey10};
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  color: ${colors.grey75};
`;

const Section = styled.section`
  margin: 0.75rem 0;

  h3 {
    font-size: 0.875rem;
    font-weight: ${weights.bold};
    margin-bottom: 0.5rem;
    color: ${colors.grey75};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const BreakDown = styled.div`
  display: flex;
  flex-direction: row;
  border-top: solid 0.125rem ${colors.grey5};
  border-bottom: solid 0.125rem ${colors.grey5};

  & + & {
    border-top: none;
  }
`;

const BreakdownItem = styled.div`
  flex: ${({ flex }) => flex || 1};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0.75rem 1.5rem;
  padding-right: 2rem;
  border-left: solid 0.125rem ${colors.grey5};
  background-color: ${({ $danger, $warning }) => ($danger ? '#fce6ef' : $warning ? colors.warning10 : 'transparent')};

  &:last-child {
    border-right: solid 0.125rem ${colors.grey5};
  }
`;

const BreakdownLabel = styled.div`
  color: ${colors.grey40};
  font-size: 0.825rem;
`;

const BreakdownValue = styled.div`
  color: ${colors.black};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
`;

const SecondaryValue = styled.div`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-style: italic;
`;

const IntegrationRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 0.25rem;
`;

const IntegrationIcon = styled.img`
  width: 1.5rem;
  height: 1.5rem;
  object-fit: contain;
  opacity: ${({ $connected }) => ($connected ? 1 : 0.2)};
  filter: ${({ $connected }) => ($connected ? 'none' : 'grayscale(100%)')};
`;

const FeatureTag = styled.span`
  display: inline-block;
  padding: 0.125rem 0.375rem;
  margin: 0.125rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  background-color: ${({ $enabled }) => ($enabled ? colors.primary10 : colors.grey5)};
  color: ${({ $enabled }) => ($enabled ? colors.primary : colors.grey40)};
  font-weight: ${({ $enabled }) => ($enabled ? weights.bold : weights.normal)};
`;

const StripeLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const InlineKey = styled.span`
  font-weight: ${weights.normal};
  font-size: 0.75rem;
  color: ${colors.grey40};
`;

const ActivityEntityRow = styled.div`
  display: flex;
  align-items: center;
  border-top: solid 0.125rem ${colors.grey5};
  border-bottom: solid 0.125rem ${colors.grey5};
  border-left: solid 0.125rem ${colors.grey5};
  border-right: solid 0.125rem ${colors.grey5};

  & + & {
    border-top: none;
  }
`;

const EntityLabel = styled.div`
  flex: 0 0 8rem;
  padding: 0.5rem 1.5rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  color: ${colors.grey75};
`;

const EntityStats = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-evenly;
  padding: 0.5rem 0;
`;

const StatColumn = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: nowrap;
`;

const EntityChart = styled.div`
  flex: 0 0 50%;
  max-width: 50%;
  padding: 0.5rem 1rem;
  height: 3.75rem;
`;

const ChartBox = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  border: solid 0.125rem ${colors.grey5};
  border-radius: 0.25rem;
`;

const ChartLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey40};
  margin-bottom: 0.25rem;
`;

const ChartEmpty = styled.div`
  height: 3.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  color: ${colors.grey25};
`;

const LoginBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const LoginBarTrack = styled.div`
  display: flex;
  height: 0.5rem;
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: ${colors.grey5};
`;

const LoginBarSegment = styled.div`
  height: 100%;
  background-color: ${({ color }) => color};
`;

const LoginBarLegend = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.6875rem;
  color: ${colors.grey55};
`;

const LegendDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const loginSegments = [
  { key: 'thisWeek', label: '< 7d', color: colors.primary },
  { key: 'thisMonth', label: '7–30d', color: colors.warning },
  { key: 'older', label: '30d+', color: colors.danger },
  { key: 'never', label: 'Never', color: colors.grey25 },
];

function LoginBar({ recency, total }) {
  if (!recency || !total) return <ChartEmpty>No active members</ChartEmpty>;

  return (
    <LoginBarContainer>
      <LoginBarTrack>
        {loginSegments.map(({ key, color }) => {
          const pct = (recency[key] / total) * 100;
          if (pct === 0) return null;
          return <LoginBarSegment key={key} color={color} style={{ width: `${pct}%` }} />;
        })}
      </LoginBarTrack>
      <LoginBarLegend>
        {loginSegments.map(({ key, label, color }) => (
          <LegendItem key={key}>
            <LegendDot color={color} />
            {label}: {recency[key]}
          </LegendItem>
        ))}
      </LoginBarLegend>
    </LoginBarContainer>
  );
}

function tzOffset(timeZoneName) {
  const now = new Date();
  const localOffset = now.getTimezoneOffset(); // in minutes, inverted sign
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone: timeZoneName, timeZoneName: 'shortOffset' });
  const parts = formatter.formatToParts(now);
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value || '';
  // Parse "GMT-5" or "GMT+5:30" style offset
  const match = offsetPart.match(/GMT([+-]?\d+(?::\d+)?)?/);
  if (!match) return '';
  const remoteMinutes = match[1] ? parseOffset(match[1]) : 0;
  const localMinutes = -localOffset;
  const diff = (remoteMinutes - localMinutes) / 60;
  if (diff === 0) return 'same as you';
  const abs = Math.abs(diff);
  const label = Number.isInteger(abs) ? `${abs}h` : `${abs.toFixed(1)}h`;
  return diff > 0 ? `${label} ahead of you` : `${label} behind you`;
}

function parseOffset(str) {
  const [h, m] = str.split(':');
  const hours = parseInt(h, 10);
  const mins = m ? parseInt(m, 10) : 0;
  return hours * 60 + (hours < 0 ? -mins : mins);
}

function daysAgo(date, prefix = '') {
  if (!date) return '';
  const days = moment().diff(moment(date), 'days');
  const p = prefix ? `${prefix} ` : '';
  if (days === 0) return `${p}today`;
  if (days === 1) return `${p}1 day ago`;
  return `${p}${days} days ago`;
}

function daysAgoSplit(date) {
  if (!date) return '-';
  const days = moment().diff(moment(date), 'days');
  if (days === 0)
    return (
      <>
        <BreakdownValue as="span">today</BreakdownValue>
      </>
    );
  if (days === 1)
    return (
      <>
        <BreakdownValue as="span">1</BreakdownValue> day ago
      </>
    );
  return (
    <>
      <BreakdownValue as="span">{days}</BreakdownValue> days ago
    </>
  );
}

const serviceTypes = {
  accounting: 'Accounting',
  advertising: 'Advertising',
  architecture: 'Architecture',
  engineering: 'Engineering',
  financial_advisors: 'Financial Advisors',
  general: 'General',
  it: 'IT Services',
  legal: 'Legal Services',
  management_consulting: 'Management Consulting',
  marketing: 'Marketing',
  public_relations: 'Public Relations',
  software_development: 'Software Development',
  web_design: 'Web Design and Development',
};

const subscriptionStatusLabels = {
  active: 'Active',
  trialing: 'Stripe Trial',
  past_due: 'Past Due',
  unpaid: 'Unpaid',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  incomplete_expired: 'Incomplete/Expired',
};

const intervalLabels = {
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Annual',
};

const teamExcludedFeatures = new Set([
  'allocations',
  'approvalWorkflow',
  'businessUnits',
  'clientApprovals',
  'forecast',
  'manualRevenueRecognition',
  'multicurrency',
  'multipleAccountingIntegrations',
  'pipeline',
  'practices',
  'projectGroups',
  'projectHealth',
  'purchaseOrders',
  'reportSchedules',
  'timesheetAttestation',
  'workspaceRoles',
]);

const featureLabels = {
  allocations: 'Allocations',
  approvalWorkflow: 'Approval Workflow',
  businessUnits: 'Business Units',
  clientAndProjectFiles: 'Client & Project Files',
  clientApprovals: 'Client Approvals',
  disciplines: 'Disciplines',
  expenseReporting: 'Expense Reporting',
  expenseMarkup: 'Expense Markup',
  forecast: 'Forecast',
  manualRevenueRecognition: 'Revenue Recognition',
  multicurrency: 'Multicurrency',
  multipleAccountingIntegrations: 'Multiple Acct. Integrations',
  pipeline: 'Pipeline',
  practices: 'Practices',
  projectGroups: 'Project Groups',
  projectHealth: 'Project Health',
  purchaseOrders: 'Purchase Orders',
  rateCards: 'Project Rate Cards',
  reportSchedules: 'Report Schedules',
  timeAttachments: 'Time Attachments',
  timesheetAttestation: 'Timesheet Attestation',
  timesheets: 'Timesheets',
  utilization: 'Utilization',
  workspaceRoles: 'Workspace Roles',
};

const integrationDefs = [
  { key: 'qbo', label: 'QuickBooks', icon: qboIcon },
  { key: 'xero', label: 'Xero', icon: xeroIcon },
  { key: 'slack', label: 'Slack', icon: slackIcon },
  { key: 'bamboo', label: 'BambooHR', icon: bambooIcon },
  { key: 'expensify', label: 'Expensify', icon: expensifyIcon },
  { key: 'salesforce', label: 'Salesforce', icon: salesforceIcon },
  { key: 'hubspot', label: 'HubSpot', icon: hubspotIcon },
  { key: 'rippling', label: 'Rippling', icon: ripplingIcon },
];

const sparklineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { display: false },
    y: { display: false },
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true, mode: 'index', intersect: false },
  },
  elements: {
    point: { radius: 0, hoverRadius: 3 },
  },
};

function ActivityEntity({ label, data, color = colors.primary }) {
  const weekLabels = useMemo(() => data.weekly.map((d) => moment(d.week).format('MMM D')), [data.weekly]);
  const weekData = useMemo(() => data.weekly.map((d) => d.count), [data.weekly]);
  const hasData = weekData.some((v) => v > 0);

  const chartData = useMemo(() => {
    if (!hasData) return null;
    return {
      labels: weekLabels,
      datasets: [
        {
          data: weekData,
          backgroundColor: color + '40',
          borderColor: color,
          borderWidth: 1.5,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [weekLabels, weekData, color, hasData]);

  return (
    <ActivityEntityRow>
      <EntityLabel>{label}</EntityLabel>
      <EntityStats>
        <StatColumn>
          <BreakdownValue>{data.recentCount.toLocaleString()}</BreakdownValue>
          <SecondaryValue>last 30 days</SecondaryValue>
        </StatColumn>
        <StatColumn>
          <BreakdownValue>{data.count.toLocaleString()}</BreakdownValue>
          <SecondaryValue>total</SecondaryValue>
        </StatColumn>
        <StatColumn>
          <BreakdownLabel>{daysAgoSplit(data.lastCreated)}</BreakdownLabel>
          <SecondaryValue>last created</SecondaryValue>
        </StatColumn>
      </EntityStats>
      <EntityChart>
        {chartData ? <Line data={chartData} options={sparklineOptions} /> : <ChartEmpty>No data</ChartEmpty>}
      </EntityChart>
    </ActivityEntityRow>
  );
}

function Sparkline({ label, labels, data, color = colors.primary, emptyMessage }) {
  const chartData = useMemo(() => {
    if (!data || data.every((v) => v === 0)) return null;
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: color + '40',
          borderColor: color,
          borderWidth: 1.5,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [labels, data, color]);

  return (
    <ChartBox>
      <ChartLabel>{label}</ChartLabel>
      {chartData ? (
        <div style={{ height: '3.75rem' }}>
          <Line data={chartData} options={sparklineOptions} />
        </div>
      ) : (
        <ChartEmpty>{emptyMessage || 'No data'}</ChartEmpty>
      )}
    </ChartBox>
  );
}

export default function General({ workspaceId }) {
  const api = useApi();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState();
  const [activity, setActivity] = useState();
  const [activityError, setActivityError] = useState(null);

  const loadActivity = useCallback(async () => {
    setActivityError(null);
    try {
      const { data } = await api.admin.workspaces.getActivity(workspaceId);
      setActivity(data);
    } catch {
      setActivity(null);
      setActivityError('Unable to load activity.');
    }
  }, [api, workspaceId]);

  useEffect(() => {
    const loadCore = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.admin.workspaces.getGeneral(workspaceId);
        setData(data);
      } finally {
        setIsLoading(false);
      }
    };
    loadCore();
    loadActivity();
    return () => {
      loadCore.cancel?.();
    };
  }, [api, workspaceId, loadActivity]);

  const planFeatures = useMemo(() => {
    if (!data?.features || !data?.billing) return [];
    const isPro = data.billing.stripeProductKey === 'pro';
    return Object.entries(data.features)
      .filter(([key]) => {
        if (!isPro && teamExcludedFeatures.has(key)) return false;
        return !!featureLabels[key];
      })
      .map(([key, enabled]) => ({ label: featureLabels[key], enabled }))
      .sort((a, b) => {
        if (a.enabled !== b.enabled) return b.enabled - a.enabled;
        return a.label.localeCompare(b.label);
      });
  }, [data]);

  if (isLoading) {
    return (
      <Details>
        <LoadingIndicator>
          <Icon icon="spinner" spin={true} />
        </LoadingIndicator>
      </Details>
    );
  }

  if (!data) return null;

  const { workspace, billing, members, dailyActiveMembers, features, integrations, adminNotes } = data;

  const trialDaysRemaining = billing?.trialEndsAt ? moment(billing.trialEndsAt).diff(moment(), 'days') : null;

  return (
    <Details>
      <Section>
        <BreakDown>
          <BreakdownItem>
            <BreakdownLabel>Service Type</BreakdownLabel>
            <BreakdownValue>
              {serviceTypes[workspace.companyServiceTypeId] || workspace.companyServiceTypeId || '-'}
            </BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Created</BreakdownLabel>
            <BreakdownValue>
              <DateTime value={workspace.createdAt} />
            </BreakdownValue>
            <SecondaryValue>{moment(workspace.createdAt).fromNow()}</SecondaryValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Time Zone</BreakdownLabel>
            <BreakdownValue>{workspace.timeZoneName || workspace.timeZoneId || '-'}</BreakdownValue>
            {workspace.timeZoneName && <SecondaryValue>{tzOffset(workspace.timeZoneName)}</SecondaryValue>}
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Currency</BreakdownLabel>
            <BreakdownValue>{workspace.currency || '-'}</BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Sample Data</BreakdownLabel>
            <BreakdownValue>{workspace.hasSampleData ? 'Yes' : 'No'}</BreakdownValue>
          </BreakdownItem>
        </BreakDown>
        <BreakDown>
          <BreakdownItem flex={2}>
            <BreakdownLabel>Integrations</BreakdownLabel>
            <IntegrationRow>
              {[...integrationDefs]
                .sort((a, b) => {
                  const ac = integrations[a.key] ? 1 : 0;
                  const bc = integrations[b.key] ? 1 : 0;
                  if (ac !== bc) return bc - ac;
                  return a.label.localeCompare(b.label);
                })
                .map(({ key, label, icon }) => (
                  <Tooltip key={key} message={`${label}${integrations[key] ? ' (connected)' : ''}`}>
                    <IntegrationIcon src={icon} alt={label} $connected={integrations[key]} />
                  </Tooltip>
                ))}
            </IntegrationRow>
          </BreakdownItem>
          {planFeatures.length > 0 && (
            <BreakdownItem flex={3}>
              <BreakdownLabel>Features</BreakdownLabel>
              <div>
                {planFeatures.map((f) => (
                  <FeatureTag key={f.label} $enabled={f.enabled}>
                    {f.label}
                  </FeatureTag>
                ))}
              </div>
            </BreakdownItem>
          )}
        </BreakDown>
        {(adminNotes?.generalNotes || adminNotes?.specialContractTerms) && (
          <BreakDown>
            {adminNotes.generalNotes && (
              <BreakdownItem style={{ flex: 1 }}>
                <BreakdownLabel style={{ marginBottom: '1rem' }}>General Notes</BreakdownLabel>
                <BreakdownValue style={{ whiteSpace: 'pre-wrap', fontWeight: 'normal', lineHeight: 1.2 }}>
                  {adminNotes.generalNotes}
                </BreakdownValue>
              </BreakdownItem>
            )}
            {adminNotes.specialContractTerms && (
              <BreakdownItem style={{ flex: 1 }}>
                <BreakdownLabel style={{ marginBottom: '1rem' }}>Special Contract Terms</BreakdownLabel>
                <BreakdownValue style={{ whiteSpace: 'pre-wrap', fontWeight: 'normal', lineHeight: 1.2 }}>
                  {adminNotes.specialContractTerms}
                </BreakdownValue>
              </BreakdownItem>
            )}
          </BreakDown>
        )}
      </Section>

      {billing && (
        <Section>
          <h3>Billing</h3>
          {billing.isRequired ? (
            <>
              <BreakDown>
                <BreakdownItem>
                  <BreakdownLabel>Plan</BreakdownLabel>
                  <BreakdownValue>
                    {{ pro: 'Pro', team: 'Team' }[billing.stripeProductKey] || billing.stripeProductKey || '-'}
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <BreakdownLabel>Interval</BreakdownLabel>
                  <BreakdownValue>{intervalLabels[billing.intervalId] || billing.intervalId || '-'}</BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <BreakdownLabel>Stripe Customer</BreakdownLabel>
                  <BreakdownValue>
                    {billing.stripeCustomerId ? (
                      <StripeLink
                        href={`https://dashboard.stripe.com/customers/${billing.stripeCustomerId}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        {billing.stripeCustomerId}
                      </StripeLink>
                    ) : (
                      '-'
                    )}
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem $danger={billing.subscriptionStatus === 'past_due'} $warning={billing.cancelAtPeriodEnd}>
                  <BreakdownLabel>Subscription</BreakdownLabel>
                  <BreakdownValue>
                    {billing.subscriptionStatus
                      ? billing.cancelAtPeriodEnd
                        ? 'Canceling'
                        : billing.subscriptionStatus === 'past_due' && billing.pastDueSince
                          ? `Past Due (${moment().diff(moment.unix(billing.pastDueSince), 'days')}d)`
                          : subscriptionStatusLabels[billing.subscriptionStatus] || billing.subscriptionStatus
                      : billing.trialEndsAt
                        ? 'Internal Trial'
                        : '-'}
                  </BreakdownValue>
                  {billing.trialEndsAt && (
                    <SecondaryValue>
                      Trial ends: <DateTime value={billing.trialEndsAt} />
                      {trialDaysRemaining !== null && (
                        <span style={{ color: trialDaysRemaining <= 7 ? colors.danger : undefined }}>
                          {' '}
                          ({trialDaysRemaining > 0 ? `${trialDaysRemaining}d remaining` : 'expired'})
                        </span>
                      )}
                    </SecondaryValue>
                  )}
                </BreakdownItem>
                <BreakdownItem $danger={billing.billingLocked}>
                  <BreakdownLabel>Locked</BreakdownLabel>
                  <BreakdownValue>{billing.billingLocked ? billing.billingLockReason : 'No'}</BreakdownValue>
                </BreakdownItem>
              </BreakDown>
              <BreakDown>
                <Tooltip
                  Container={BreakdownItem}
                  message={
                    <>
                      Min members: <strong>{billing.minMembers || 0}</strong>
                      <br />
                      Limit non-bill: <strong>{billing.limitNonbill ? 'Yes' : 'No'}</strong>
                      {billing.associatedWorkspaces?.length > 0 && (
                        <>
                          <br />
                          <br />
                          Handles billing for:
                          {billing.associatedWorkspaces.map((ws) => (
                            <React.Fragment key={ws.id}>
                              <br />
                              &bull; <strong>{ws.name}</strong> ({ws.key})
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </>
                  }>
                  <BreakdownLabel>Billable Seats</BreakdownLabel>
                  <BreakdownValue>
                    {members.seatCount}{' '}
                    <span
                      style={{
                        fontSize: '0.75rem',
                        color: { 1: colors.success, 2: colors.warning, 3: colors.grey55 }[members.tier],
                      }}>
                      (Tier {members.tier})
                    </span>
                  </BreakdownValue>
                </Tooltip>
                <BreakdownItem>
                  <BreakdownLabel>Seat Price</BreakdownLabel>
                  <BreakdownValue>
                    {billing.seatPrice != null ? `$${billing.seatPrice.toFixed(2)} / mo` : '-'}
                    {billing.stripePriceKey && (
                      <>
                        {' '}
                        <InlineKey>({billing.stripePriceKey})</InlineKey>
                      </>
                    )}
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <BreakdownLabel>Lifetime Value</BreakdownLabel>
                  <BreakdownValue>
                    {`$${(billing.lifetimeValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <BreakdownLabel>Collection</BreakdownLabel>
                  <BreakdownValue>
                    {billing.stripeCustomerId && billing.collectionMethod === 'send_invoice'
                      ? 'Invoice'
                      : 'Credit Card'}
                    {billing.stripeCustomerId && billing.collectionMethod !== 'send_invoice' && (
                      <>
                        {' '}
                        <Icon
                          icon={billing.hasPaymentMethod ? 'check' : 'times'}
                          color={billing.hasPaymentMethod ? colors.green : colors.danger}
                          style={{ fontSize: '0.75rem' }}
                        />
                      </>
                    )}
                  </BreakdownValue>
                </BreakdownItem>
                <BreakdownItem>
                  <BreakdownLabel>Balance</BreakdownLabel>
                  <BreakdownValue>{`$${((billing.customerBalance || 0) / 100).toFixed(2)}`}</BreakdownValue>
                </BreakdownItem>
              </BreakDown>
            </>
          ) : (
            <BreakDown>
              <BreakdownItem>
                <BreakdownLabel>Plan</BreakdownLabel>
                <BreakdownValue>
                  {{ pro: 'Pro', team: 'Team' }[billing.stripeProductKey] || billing.stripeProductKey || '-'}
                </BreakdownValue>
              </BreakdownItem>
              <BreakdownItem>
                <BreakdownLabel>Billing Required</BreakdownLabel>
                <BreakdownValue>No</BreakdownValue>
              </BreakdownItem>
              <BreakdownItem>
                <BreakdownLabel>Sandbox</BreakdownLabel>
                <BreakdownValue>{billing.isSandbox ? 'Yes' : 'No'}</BreakdownValue>
              </BreakdownItem>
              <BreakdownItem>
                <BreakdownLabel>Associated Workspace</BreakdownLabel>
                <BreakdownValue>
                  {billing.associatedWorkspaceName ? (
                    <>
                      {billing.associatedWorkspaceName} <InlineKey>({billing.associatedWorkspaceKey})</InlineKey>
                    </>
                  ) : (
                    '-'
                  )}
                </BreakdownValue>
              </BreakdownItem>
            </BreakDown>
          )}
        </Section>
      )}

      <Section>
        <h3>Members</h3>
        <BreakDown>
          <BreakdownItem>
            <BreakdownLabel>Active Billable</BreakdownLabel>
            <BreakdownValue>{members.activeBillable}</BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Active Non-Billable</BreakdownLabel>
            <BreakdownValue>{members.activeNonBillable}</BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Total Active</BreakdownLabel>
            <BreakdownValue>{members.totalActive}</BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Inactive</BreakdownLabel>
            <BreakdownValue>{members.inactive}</BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Verified</BreakdownLabel>
            <BreakdownValue>{members.verified}</BreakdownValue>
          </BreakdownItem>
          <BreakdownItem>
            <BreakdownLabel>Unverified</BreakdownLabel>
            <BreakdownValue>{members.unverified}</BreakdownValue>
          </BreakdownItem>
        </BreakDown>

        <BreakDown>
          <BreakdownItem>
            <BreakdownLabel>Last Login</BreakdownLabel>
            <BreakdownValue>{members.lastLogin ? <DateTime value={members.lastLogin} /> : '-'}</BreakdownValue>
            {members.lastLogin && <SecondaryValue>{daysAgo(members.lastLogin)}</SecondaryValue>}
          </BreakdownItem>
          <BreakdownItem flex={4}>
            <BreakdownLabel>Active Member Logins</BreakdownLabel>
            <LoginBar recency={members.loginRecency} total={members.totalActive} />
          </BreakdownItem>
        </BreakDown>

        <div style={{ marginTop: '0.75rem' }}>
          <Sparkline
            label="Daily Active Members (90d)"
            labels={dailyActiveMembers?.map((d) => d.date)}
            data={dailyActiveMembers?.map((d) => d.count)}
            emptyMessage={dailyActiveMembers === null ? 'No activity data available' : 'No data'}
          />
        </div>
      </Section>

      <Section>
        <h3>Activity</h3>
        {activity ? (
          <>
            {features?.pipeline && (
              <ActivityEntity label="Opportunities" data={activity.opportunities} color={colors.primary} />
            )}
            <ActivityEntity label="Clients" data={activity.clients} color={colors.primary} />
            <ActivityEntity label="Projects" data={activity.projects} color={colors.green} />
            <ActivityEntity label="Tasks" data={activity.tasks} color={colors.green} />
            <ActivityEntity label="Time Entries" data={activity.timeEntries} color={colors.green} />
            {features?.expenseReporting && (
              <ActivityEntity label="Expenses" data={activity.expenses} color={colors.warning} />
            )}
            {features?.allocations && (
              <ActivityEntity label="Allocations" data={activity.allocations} color={colors.primary} />
            )}
            <ActivityEntity label="Invoices" data={activity.invoices} color={colors.warning} />
            <ActivityEntity label="Payments" data={activity.payments} color={colors.success} />
          </>
        ) : activityError ? (
          <LoadingIndicator>
            {activityError}{' '}
            <Button isOutline={true} onClick={loadActivity}>
              Retry
            </Button>
          </LoadingIndicator>
        ) : (
          <LoadingIndicator>
            <Icon icon="spinner" spin={true} />
          </LoadingIndicator>
        )}
      </Section>
    </Details>
  );
}
